/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Mon May 27 2024
 **/

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useFileManager } from "@/frameworks/core/hooks";
import { useToast } from "@/frameworks/designStuff/components";
import { _T } from "@rfice/language";

type DeleteFileOrFolderVariables = (
	| FileBox.Files.DeleteFileRequest
	| FileBox.Folders.DeleteFolderRequest
	| FileBox.Link.RemoveLinkRequest
) & {
	fileBoxType: Required<FileBox.Folders.GetFolderFileListResponse>["content"][number]["fileBoxType"];
};

export default function useDeleteFile({ enableToast = true }: { enableToast?: boolean } = {}) {
	const toast = useToast();
	const queryClient = useQueryClient();
	const { deleteFile, deleteFolder, removeLink, currentFolder } = useFileManager();

	const { mutateAsync: deleteFileOrFolder } = useMutation({
		mutationKey: ["deleteFile"],
		mutationFn: (options: DeleteFileOrFolderVariables) => {
			switch (options.fileBoxType) {
				case "FILE": {
					return deleteFile(options);
				}
				case "FOLDER": {
					return deleteFolder(options);
				}
				case "LINK": {
					return removeLink(options);
				}
				default: {
					return deleteFile(options);
				}
			}
		},
		onSuccess(_, variables) {
			//TODO @민순기 성능 최적화를 위해 api 재호출이 아닌 클라이언트단 로직 필요
			queryClient.refetchQueries({
				queryKey: ["folderFileList", { all: currentFolder.all, id: currentFolder.id }]
			});
			queryClient.setQueryData<FileBox.Folders.GetMyFoldersResponse>(["myFolders", currentFolder.id], (prev) => {
				if (!prev) return prev;
				return [...prev].filter((v) => v.id !== variables.id);
			});
			if (enableToast) toast.show({ type: "system", message: _T("삭제되었습니다.") });
		}
	});

	return deleteFileOrFolder;
}
